import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChartPieIcon,
  CloudArrowUpIcon,
  CogIcon,
  DocumentCheckIcon,
  FunnelIcon,
  PaperClipIcon,
} from '@heroicons/react/20/solid';
import hlxFeaturesScreenshot from '~/assets/features-hlx.webp';
import mcvFeaturesScreenshot from '~/assets/features-mcv.webp';
import type { SiteBrand } from '~/config/branding';

export type FeatureItem = {
  name: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

export type FeaturesContent = {
  eyebrow: string;
  headline: string;
  features: FeatureItem[];
  screenshot: { alt: string; src: string };
};

// Per-brand features copy. MedCredVault leads with credential management;
// HealthcareLX leads with the labor-exchange marketplace capabilities.
export const FEATURES_CONTENT: Record<SiteBrand, FeaturesContent> = {
  mcv: {
    eyebrow: 'MedCredVault',
    headline: 'Everything you need to manage credentials',
    features: [
      {
        name: 'Credential & CV Management.',
        description: 'Secure document storage, expiration tracking, and CV upload/parse.',
        icon: PaperClipIcon,
      },
      {
        name: 'Auto-Population.',
        description:
          'Integrations with NPPES, PECOS, and state boards, with plans for international and specialty expansion.',
        icon: CloudArrowUpIcon,
      },
      {
        name: 'AI & Analytics.',
        description: 'Compensation benchmarking, productivity metrics, and career insights.',
        icon: CogIcon,
      },
      {
        name: 'Job Matching.',
        description: 'Personalized job recommendations via HealthcareLX.',
        icon: FunnelIcon,
      },
      {
        name: 'CME Tracking.',
        description: 'State-specific requirements, progress tracking, and reminders.',
        icon: ChartPieIcon,
      },
    ],
    screenshot: {
      src: mcvFeaturesScreenshot,
      alt: 'MedCredVault dashboard screenshot',
    },
  },
  hlx: {
    eyebrow: 'Healthcare Labor Exchange',
    headline: 'Everything you need to exchange healthcare labor',
    features: [
      {
        name: 'Labor Marketplace.',
        description:
          'Discover, publish, and exchange shifts, FTEs, locum coverage, physician groups, and enterprise workforce solutions in one place.',
        icon: FunnelIcon,
      },
      {
        name: 'Standardized Contracting.',
        description:
          'Transparent negotiations, common labor units, and consistent agreements across every workforce model.',
        icon: DocumentCheckIcon,
      },
      {
        name: 'Workforce Intelligence™.',
        description:
          'AI-powered matching, compensation benchmarking, demand forecasting, and decision support.',
        icon: CogIcon,
      },
      {
        name: 'Real-Time Availability.',
        description:
          'Calendar-based scheduling and intelligent recommendations that connect the right participants at the right time.',
        icon: CalendarDaysIcon,
      },
      {
        name: 'Full Lifecycle Management.',
        description:
          'From discovery and contracting through scheduling, invoicing, performance tracking, and renewals.',
        icon: ArrowPathIcon,
      },
    ],
    screenshot: {
      src: hlxFeaturesScreenshot,
      alt: 'HealthcareLX B2B job search screenshot',
    },
  },
};
